<template>
  <div class="pcPage">
    <div class="pcPage__mapWrap">
      <div class="pcPage__regionBar">
        <div class="pcPage__regionTabs">
          <div
            class="pcPage__regionTabs__slider"
            :class="
              mapRegion === MAP_REGION_CN
                ? 'pcPage__regionTabs__slider--cn'
                : 'pcPage__regionTabs__slider--my'
            "
          />
          <button
            type="button"
            class="pcPage__regionTab"
            :class="{
              'pcPage__regionTab--active': mapRegion === MAP_REGION_CN,
              'pcPage__regionTab--disabled': isRegionTabSwitchDisabled(MAP_REGION_CN),
            }"
            @click="switchCountry(MAP_REGION_CN)"
          >
            <span class="pcPage__regionTab__flag" aria-hidden="true">🇨🇳</span>
            <span class="pcPage__regionTab__text">{{
              $t("app.home.map_country_china")
            }}</span>
          </button>
          <button
            type="button"
            class="pcPage__regionTab"
            :class="{
              'pcPage__regionTab--active': mapRegion === MAP_REGION_MY,
              'pcPage__regionTab--disabled': isRegionTabSwitchDisabled(MAP_REGION_MY),
            }"
            @click="switchCountry(MAP_REGION_MY)"
          >
            <span class="pcPage__regionTab__flag" aria-hidden="true">🇲🇾</span>
            <span class="pcPage__regionTab__text">{{
              $t("app.home.map_country_malaysia")
            }}</span>
          </button>
        </div>
      </div>

      <TianDiTuAllStoresMap
        v-if="useTiandituMap"
        class="pcPage__map"
        ref="mapRef"
        :dealers="dealerList"
        :mapRegion="mapRegion"
        :activeKey="activeDealerKey"
        :pcOverview="true"
        @map-ready="mapReady = true"
        @marker-click="onMarkerClick"
      />
      <MapTilerAllStoresMap
        v-else
        class="pcPage__map"
        ref="mapRef"
        :dealers="dealerList"
        :mapRegion="mapRegion"
        :activeKey="activeDealerKey"
        :pcOverview="true"
        :mapLabelLanguage="mapLabelLanguage"
        @map-ready="mapReady = true"
        @marker-click="onMarkerClick"
      />

      <div v-if="!mapReady" class="pcPage__mapLoading">
        <div class="pcPage__mapLoading__spinner" />
        <span>{{ $t("app.home.map_loading") }}</span>
      </div>

      <div class="pcPage__float">
        <MapTilerAllStoresPanel
          :groups="groupedDealers"
          :loading="listLoading"
          v-model:keyword="keyword"
          :activeKey="activeDealerKey"
          @select="onSelectDealerFromList"
          @overview="onResetMapOverview"
        />
      </div>

      <div
        v-if="showMarkerPopup && selectDealerInfo.fullName"
        class="pcPopupMask"
        @click.self="showMarkerPopup = false"
      >
        <div class="pcPopup" @click.stop>
          <van-icon
            name="cross"
            class="pcPopup__close"
            @click="showMarkerPopup = false"
          />
          <div class="pcPopup__title">{{ selectDealerInfo.fullName }}</div>
          <div
            v-if="selectDealerInfo.regionText"
            class="pcPopup__regionText"
          >
            {{ selectDealerInfo.regionText }}
          </div>
          <div v-if="popupDealerList.length > 1" class="pcPopup__switchList">
            <div
              v-for="(d, idx) in popupDealerList"
              :key="d.__listKey ?? `${d.fullName}-${idx}`"
              class="pcPopup__switchItem"
              :class="{
                'pcPopup__switchItem--active': idx === popupDealerIndex,
              }"
              @click="onPickPopupDealer(d, idx)"
            >
              {{ d.fullName || "-" }}
            </div>
          </div>
          <div class="pcPopup__row" v-if="selectDealerInfo.address">
            <img class="pcPopup__icon" :src="pcIconLoc" alt="" />
            <div class="pcPopup__text">{{ selectDealerInfo.address }}</div>
            <button
              type="button"
              class="pcPopup__copyBtn"
              @click="onCopyAddress"
            >
              {{ $t("app.home.map_popup_copy_address") }}
            </button>
          </div>
          <button type="button" class="pcPopup__btn" @click="onGotoPlace">
            <img class="pcPopup__btnIcon" :src="pcIconNav" alt="" />
            <span>{{ $t("app.home.map_popup_nav_to") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import MapTilerAllStoresMap from "@/views/home/maptiler/MapTilerAllStoresMap.vue";
import TianDiTuAllStoresMap from "@/views/home/maptiler/TianDiTuAllStoresMap.vue";
import MapTilerAllStoresPanel from "@/views/home/maptiler/MapTilerAllStoresPanel.vue";
import {
  groupDealersByCountryProvince,
  getDealerItemKey,
  getDealerDisplayAddress,
  hasValidMapCoordinates,
  isMalaysiaDealer,
  resolveDealerIndexInGroup,
} from "@/views/home/maptiler/useMapTilerAllStores";
import { useMapTilerCopyText } from "@/views/home/maptiler/useMapTilerCopyText";
import { useRouteLangQueryEffect } from "@/views/home/maptiler/useRouteLangQueryEffect";
import {
  getDefaultMapRegion,
  shouldUseAmapForNavigation,
  shouldUseTiandituMap,
} from "@/views/home/maptiler/useMapTilerSite";
import { useMapTilerRegionSwitch } from "@/views/home/maptiler/useMapTilerRegionSwitch";
import { useMapTilerCountryTabSwitch } from "@/views/home/maptiler/useMapTilerCountryTabSwitch";
import { useMapLabelLanguage } from "@/views/home/maptiler/useMapLabelLanguage";
import {
  getChinaProvinceDisplayName,
  localizeGroupedDealersForDisplay,
} from "@/views/home/maptiler/provinceDisplay";
import {
  MAP_REGION_CN,
  MAP_REGION_MY,
  getMapRegionDefaultLocation,
} from "@/utils/config";
import navigatorUtils from "@/utils/navigatorUtilsOptimized";
import pcIconLoc from "@/assets/map-icons/map_location_theme.png";
import pcIconNav from "@/assets/map-icons/navigation.png";

import { useMapTilerNavigationGeolocation } from "@/views/home/maptiler/useMapTilerNavigationGeolocation";

const useNavigatorEffect = (
  selectDealerInfo,
  mapRegion,
  locale,
  requestOriginForNavigation,
  dismissMarkerPopup
) => {
  const onGotoPlace = async () => {
    const lang = locale.value === "en-US" ? "en" : "zh";
    const useAmap = shouldUseAmapForNavigation();
    let navOptions = { lang, skipInternalGeolocation: true };

    dismissMarkerPopup?.();
    await nextTick();
    const { origin, navigationAfterAsync, cancelled } =
      await requestOriginForNavigation();
    if (cancelled) return;
    navOptions = {
      ...navOptions,
      originWgs84: origin,
      navigationAfterAsync,
    };

    navigatorUtils.navigateToDestinationPc(
      {
        endLatitude: selectDealerInfo.endLatitude,
        endLongitude: selectDealerInfo.endLongitude,
        name: selectDealerInfo.fullName,
        region: mapRegion.value,
        useAmap,
      },
      navOptions
    );
  };
  return { onGotoPlace };
};

export default {
  name: "HomePcMapTiler",
  components: {
    MapTilerAllStoresMap,
    TianDiTuAllStoresMap,
    MapTilerAllStoresPanel,
  },
  setup() {
    const { locale } = useI18n();
    useRouteLangQueryEffect();
    const { mapLabelLanguage } = useMapLabelLanguage();

    const mapRef = ref(null);
    const mapRegion = ref(getDefaultMapRegion());
    const mapReady = ref(false);
    const listLoading = ref(false);
    const dealerList = ref([]);
    const keyword = ref("");
    const activeDealerKey = ref(null);
    const selectDealerInfo = reactive({});
    const showMarkerPopup = ref(false);
    const popupDealerList = ref([]);
    const popupDealerIndex = ref(0);
    const useTiandituMap = shouldUseTiandituMap();

    const { copyText } = useMapTilerCopyText();

    const groupedDealers = computed(() =>
      localizeGroupedDealersForDisplay(
        groupDealersByCountryProvince(dealerList.value),
        locale.value,
        mapRegion.value
      )
    );

    const innerDefaultMapCenter = reactive({
      ...getMapRegionDefaultLocation(mapRegion.value),
    });

    const { requestOriginForNavigation } = useMapTilerNavigationGeolocation();

    const { onGotoPlace } = useNavigatorEffect(
      selectDealerInfo,
      mapRegion,
      locale,
      requestOriginForNavigation,
      () => {
        showMarkerPopup.value = false;
      }
    );

    const { switchCountry: switchCountryBase, loadInitialDealers } =
      useMapTilerRegionSwitch({
      mapRegion,
      dealerList,
      listLoading,
      keyword,
      activeDealerKey,
      showMarkerPopup,
      innerDefaultMapCenter,
    });

    const { switchCountry, isRegionTabSwitchDisabled } =
      useMapTilerCountryTabSwitch({
        switchCountryBase,
        listLoading,
        mapRegion,
      });

    const focusDealerOnMap = (item) => {
      if (!hasValidMapCoordinates(item) || !mapRef.value?.focusDealer) return;
      const lat = item?.latitude ?? item?.lat;
      const lng = item?.longitude ?? item?.lng;
      mapRef.value.focusDealer({
        longitude: Number(lng),
        latitude: Number(lat),
        dealer: item,
      });
    };

    const buildRegionText = (dealer) => {
      if (isMalaysiaDealer(dealer)) {
        return [dealer?.provinceName, dealer?.cityName, dealer?.districtName]
          .filter(Boolean)
          .join(" · ");
      }
      const province = getChinaProvinceDisplayName(
        dealer?.provinceName,
        locale.value
      );
      const parts = [dealer?.countryName, province].filter(Boolean);
      return parts.join(" · ");
    };

    const syncPopupDealerInfo = (dealer) => {
      if (!dealer) return;
      selectDealerInfo.fullName = dealer.fullName;
      selectDealerInfo.address = getDealerDisplayAddress(dealer);
      selectDealerInfo.regionText = buildRegionText(dealer);
      selectDealerInfo.endLatitude =
        dealer.latitude ?? dealer.endLatitude ?? dealer.lat;
      selectDealerInfo.endLongitude =
        dealer.longitude ?? dealer.endLongitude ?? dealer.lng;
    };

    const onCopyAddress = () => {
      copyText(selectDealerInfo.address);
    };

    const onSelectDealerFromList = ({ item, key }) => {
      activeDealerKey.value = key;
      if (!item) return;
      focusDealerOnMap(item);
    };

    const onResetMapOverview = () => {
      activeDealerKey.value = null;
      showMarkerPopup.value = false;
      mapRef.value?.resetOverview?.();
    };

    const onPickPopupDealer = (dealer, idx) => {
      popupDealerIndex.value = idx;
      syncPopupDealerInfo(dealer);
      activeDealerKey.value = dealer?.__listKey ?? null;
    };

    const onMarkerClick = (location) => {
      if (!location || location.id !== "dealer") {
        showMarkerPopup.value = false;
        return;
      }

      const lat = Number(location.endLatitude ?? location.latitude);
      const lng = Number(location.endLongitude ?? location.longitude);
      const groupedFromMarker = Array.isArray(location.groupDealers)
        ? location.groupDealers
        : [];
      const groupedFromList = (dealerList.value || []).filter((d) => {
        const dLat = Number(d.latitude ?? d.lat);
        const dLng = Number(d.longitude ?? d.lng);
        if (!Number.isFinite(dLat) || !Number.isFinite(dLng)) return false;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        return (
          dLat.toFixed(6) === lat.toFixed(6) && dLng.toFixed(6) === lng.toFixed(6)
        );
      });
      const grouped =
        groupedFromMarker.length > 0 ? groupedFromMarker : groupedFromList;

      popupDealerList.value =
        grouped.length > 0
          ? grouped
          : [
              {
                fullName: location.fullName,
                address: location.address,
                latitude: location.endLatitude ?? location.latitude,
                longitude: location.endLongitude ?? location.longitude,
                __listKey: getDealerItemKey(location, 0),
              },
            ];
      popupDealerIndex.value = resolveDealerIndexInGroup(
        popupDealerList.value,
        activeDealerKey.value
      );
      syncPopupDealerInfo(popupDealerList.value[popupDealerIndex.value]);
      activeDealerKey.value =
        popupDealerList.value[popupDealerIndex.value]?.__listKey ?? null;
      showMarkerPopup.value = true;
    };

    onMounted(() => {
      loadInitialDealers();
    });

    return {
      MAP_REGION_CN,
      MAP_REGION_MY,
      mapRegion,
      mapReady,
      listLoading,
      switchCountry,
      isRegionTabSwitchDisabled,
      dealerList,
      groupedDealers,
      keyword,
      activeDealerKey,
      mapRef,
      useTiandituMap,
      mapLabelLanguage,
      onSelectDealerFromList,
      onResetMapOverview,
      onMarkerClick,
      onGotoPlace,
      selectDealerInfo,
      pcIconLoc,
      pcIconNav,
      showMarkerPopup,
      popupDealerList,
      popupDealerIndex,
      onPickPopupDealer,
      onCopyAddress,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/viriables.scss";

.pcPage {
  position: absolute;
  inset: 0;
  background: #e8e8e8;

  &__mapWrap {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__regionBar {
    position: absolute;
    left: 0.16rem;
    top: 0.16rem;
    z-index: 2100;
    padding: 0.04rem;
    border-radius: 0.14rem;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 0.06rem 0.2rem rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(8px);
  }

  &__regionTabs {
    position: relative;
    display: flex;
    width: 2.56rem;
    height: 0.36rem;
    padding: 0.03rem;
    border-radius: 0.1rem;
    background: #f3f5f4;
    box-sizing: border-box;
  }

  &__regionTabs__slider {
    position: absolute;
    top: 0.03rem;
    bottom: 0.03rem;
    width: calc(50% - 0.03rem);
    border-radius: 0.08rem;
    background: linear-gradient(135deg, #00a34a 0%, $themeColor 100%);
    box-shadow: 0 0.02rem 0.08rem rgba(0, 147, 67, 0.35);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
  }

  &__regionTabs__slider--cn {
    transform: translateX(0);
  }

  &__regionTabs__slider--my {
    transform: translateX(calc(100% + 0.03rem));
  }

  &__regionTab {
    position: relative;
    z-index: 1;
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.05rem;
    border: none;
    background: transparent;
    color: #666;
    font-size: 0.12rem;
    line-height: 1;
    cursor: pointer;
    transition: color 0.2s ease;
    padding: 0;

    &__flag {
      font-size: 0.14rem;
      line-height: 1;
    }

    &__text {
      font-weight: 500;
    }

    &--active {
      color: #fff;
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  &__mapLoading {
    position: absolute;
    inset: 0;
    z-index: 1500;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.12rem;
    background: rgba(245, 247, 246, 0.72);
    color: #666;
    font-size: 0.12rem;
    pointer-events: none;

    &__spinner {
      width: 0.28rem;
      height: 0.28rem;
      border: 0.03rem solid rgba(0, 147, 67, 0.15);
      border-top-color: $themeColor;
      border-radius: 50%;
      animation: pcMapSpin 0.8s linear infinite;
    }
  }

  &__map {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  &__float {
    position: absolute;
    left: 0.16rem;
    top: 0.64rem;
    bottom: 0.16rem;
    width: 4.2rem;
    z-index: 2000;
    pointer-events: auto;
    max-height: calc(100% - 0.8rem);
  }
}

@keyframes pcMapSpin {
  to {
    transform: rotate(360deg);
  }
}

.pcPopupMask {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  padding: 0.24rem;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.pcPopup {
  position: relative;
  width: 3.6rem;
  max-width: 100%;
  max-height: min(85vh, calc(100vh - 0.48rem));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  margin: auto;
  background: #fff;
  border-radius: 0.12rem;
  box-shadow: 0 0.06rem 0.24rem rgba(0, 0, 0, 0.18);
  padding: 0.18rem 0.16rem 0.16rem;
  box-sizing: border-box;

  &__close {
    position: absolute;
    right: 0.1rem;
    top: 0.1rem;
    font-size: 0.18rem;
    color: #999;
    cursor: pointer;
  }

  &__title {
    font-size: 0.14rem;
    font-weight: 700;
    color: #383838;
    line-height: 1.25;
    padding-right: 0.28rem;
  }

  &__regionText {
    margin-top: 0.06rem;
    font-size: 0.11rem;
    color: $themeColor;
  }

  &__switchList {
    margin-top: 0.1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.08rem;
    max-width: 100%;
  }

  &__switchItem {
    padding: 0.04rem 0.08rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.12rem;
    background: #fff;
    color: #383838;
    font-size: 0.11rem;
    line-height: 1.2;
    cursor: pointer;
    max-width: 100%;
    word-break: break-word;
  }

  &__switchItem--active {
    border-color: #009343;
    color: #009343;
    background: rgba(0, 147, 67, 0.08);
  }

  &__row {
    display: flex;
    gap: 0.08rem;
    margin-top: 0.12rem;
    align-items: flex-start;
  }

  &__icon {
    width: 0.16rem;
    height: 0.16rem;
    margin-top: 0.02rem;
    flex-shrink: 0;
    object-fit: contain;
  }

  &__text {
    flex: 1;
    font-size: 0.12rem;
    color: #383838;
    line-height: 1.25;
    word-break: break-all;
  }

  &__copyBtn {
    flex-shrink: 0;
    border: none;
    background: rgba(0, 147, 67, 0.08);
    color: $themeColor;
    font-size: 0.1rem;
    line-height: 1;
    padding: 0.05rem 0.08rem;
    border-radius: 0.04rem;
    cursor: pointer;

    &:hover {
      background: rgba(0, 147, 67, 0.14);
    }
  }

  &__btn {
    width: 100%;
    margin-top: 0.16rem;
    height: 0.42rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.06rem;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #00a34a 0%, $themeColor 100%);
    color: #fff;
    font-size: 0.14rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 0.04rem 0.14rem rgba(0, 147, 67, 0.38);
    transition: opacity 0.15s ease, transform 0.15s ease;

    &:hover {
      opacity: 0.94;
    }

    &:active {
      transform: scale(0.98);
      opacity: 0.92;
    }
  }

  &__btnIcon {
    width: 0.14rem;
    height: 0.14rem;
    flex-shrink: 0;
    filter: brightness(0) invert(1);
  }
}
</style>
